import { ComponentProps, useMemo } from "react";

import cn from "@/helpers/classNames";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

export default function Pagination({
  canNextPage,
  canPreviousPage,
  gotoPage,
  pageCount,
  pageIndex,
  className,
}: {
  canNextPage: boolean;
  canPreviousPage: boolean;
  gotoPage: (n: number) => void;
  pageCount: number;
  pageIndex: number;
  className?: string;
}) {
  const pages = useMemo(() => {
    const pages = [...Array(pageCount).keys()].map((pageIndex) => ({
      value: pageIndex,
      display: `${pageIndex + 1}`,
    }));

    if (pageCount <= 5) {
      return pages;
    }

    // First page.
    if (pageIndex === 0)
      return [
        ...pages.slice(0, 3),
        { value: null, display: "..." },
        ...pages.slice(pages.length - 1),
      ];

    // Last two pages.
    if (pageIndex > pageCount - 3)
      return [
        ...pages.slice(0, 1),
        { value: null, display: "..." },
        ...pages.slice(pages.length - 3),
      ];

    return [
      ...pages.slice(pageIndex - 1, pageIndex + 2),
      { value: null, display: "..." },
      ...pages.slice(pages.length - 1),
    ];
  }, [pageCount, pageIndex]);

  return (
    <div className={cn("flex justify-end", className)}>
      <div className="flex divide-x rounded-xs border bg-background">
        <div>
          <PaginationButton
            disabled={!canPreviousPage}
            onClick={() => gotoPage(pageIndex - 1)}
          >
            <ChevronLeftIcon className="size-4" />
          </PaginationButton>
        </div>
        {pages.map((page) => (
          <div key={page.display}>
            <PaginationButton
              active={page.value === pageIndex}
              onClick={() => {
                if (typeof page.value !== "number") {
                  return;
                }

                gotoPage(page.value);
              }}
              disabled={typeof page.value !== "number"}
            >
              {page.display}
            </PaginationButton>{" "}
          </div>
        ))}
        <div>
          <PaginationButton
            disabled={!canNextPage}
            onClick={() => gotoPage(pageIndex + 1)}
          >
            <ChevronRightIcon className="size-4" />
          </PaginationButton>
        </div>
      </div>
    </div>
  );
}

function PaginationButton({
  active,
  ...buttonProps
}: ComponentProps<"button"> & {
  active?: boolean;
}) {
  return (
    <button
      {...buttonProps}
      className={cn(
        "flex size-8 items-center justify-center text-sm text-muted-foreground select-none hover:bg-gray-100 focus:outline-hidden dark:hover:bg-gray-900",
        {
          "pointer-events-none bg-gray-100 text-foreground dark:bg-gray-900 dark:text-white":
            active,
          "pointer-events-none opacity-50": buttonProps.disabled,
        },
        buttonProps.className,
      )}
    >
      {buttonProps.children}
    </button>
  );
}
